import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import LocationPicker from './components/LocationPicker';

export default function PlotManagement() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [plots, setPlots] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlot, setEditingPlot] = useState(null);
  const [location, setLocation] = useState(null);
  
  const [formData, setFormData] = useState({
    plot_name: '',
    area_ha: '',
    soil_type: '',
    primary_crop: ''
  });

  useEffect(() => {
    fetchPlots();
  }, []);

  const fetchPlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://agroconnect.io/api/plots', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      console.log('📊 Plots data:', data);
      if (data.ok) {
        setPlots(data.rows || []);
      }
    } catch (error) {
      console.error('Error fetching plots:', error);
    }
  };

  const handleLocationCapture = (locationData) => {
    setLocation(locationData);
    console.log('📍 Plot location captured:', locationData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      plot_name: formData.plot_name,
      area_ha: parseFloat(formData.area_ha) || 0,
      soil_type: formData.soil_type || '',
      primary_crop: formData.primary_crop || '',
      ...(location && {
        gps_lat: location.latitude,
        gps_lng: location.longitude
      })
    };

    console.log('📍 Plot payload:', payload);

    try {
      const token = localStorage.getItem('token');
      const url = editingPlot 
        ? `https://agroconnect.io/api/plots/${editingPlot.plot_id}`
        : 'https://agroconnect.io/api/plots';
      
      const response = await fetch(url, {
        method: editingPlot ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('📊 Server response:', data);
      
      if (data.ok) {
        console.log('✅ Plot saved successfully');
        await fetchPlots();
        resetForm();
      } else {
        console.error('❌ Error saving plot:', data.error);
        alert(data.error || 'Failed to save plot');
      }
    } catch (error) {
      console.error('Error submitting plot:', error);
      alert('Failed to save plot: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      plot_name: '',
      area_ha: '',
      soil_type: '',
      primary_crop: ''
    });
    setLocation(null);
    setEditingPlot(null);
    setShowForm(false);
  };

  const handleEdit = (plot) => {
    setEditingPlot(plot);
    setFormData({
      plot_name: plot.plot_name || '',
      area_ha: plot.area_ha || '',
      soil_type: plot.soil_type || '',
      primary_crop: plot.primary_crop || ''
    });
    if (plot.gps_lat && plot.gps_lng) {
      setLocation({
        latitude: plot.gps_lat,
        longitude: plot.gps_lng
      });
    }
    setShowForm(true);
  };

  const handleDelete = async (plotId) => {
    if (!window.confirm('Are you sure you want to delete this plot?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://agroconnect.io/api/plots/${plotId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.ok) {
        fetchPlots();
      }
    } catch (error) {
      console.error('Error deleting plot:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div style={{
      background: '#1a3327',
      padding: '24px',
      borderRadius: '8px',
      border: '1px solid #2d5a45'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#4ade80' }}>My Plots</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '10px 20px',
            background: '#4ade80',
            color: '#0f2419',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          {showForm ? 'Cancel' : '+ Add Plot'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: '30px' }}>
          <div style={{
            background: '#0f2419',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #2d5a45'
          }}>
            <h3 style={{ marginTop: 0, color: '#4ade80' }}>
              {editingPlot ? 'Edit Plot' : 'New Plot'}
            </h3>

            {/* GPS Location Section */}
            <div style={{ marginBottom: '20px' }}>
              <LocationPicker 
                onLocationCapture={handleLocationCapture}
                label="📍 Plot GPS Location"
                autoCapture={false}
              />
            </div>

            {/* Form Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#4ade80' }}>
                  Plot Name *
                </label>
                <input
                  type="text"
                  name="plot_name"
                  value={formData.plot_name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #2d5a45',
                    background: '#1a3327',
                    color: 'white'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#4ade80' }}>
                  Size (hectares) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="area_ha"
                  value={formData.area_ha}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #2d5a45',
                    background: '#1a3327',
                    color: 'white'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#4ade80' }}>
                  Soil Type
                </label>
                <input
                  type="text"
                  name="soil_type"
                  value={formData.soil_type}
                  onChange={handleChange}
                  placeholder="e.g., Clay, Loam, Sandy"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #2d5a45',
                    background: '#1a3327',
                    color: 'white'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', color: '#4ade80' }}>
                  Primary Crop
                </label>
                <input
                  type="text"
                  name="primary_crop"
                  value={formData.primary_crop}
                  onChange={handleChange}
                  placeholder="e.g., Maize, Coffee, Tea"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #2d5a45',
                    background: '#1a3327',
                    color: 'white'
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '12px 24px',
                  background: location ? '#4ade80' : '#6b7280',
                  color: location ? '#0f2419' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {location ? '📍 Save Plot with GPS' : 'Save Plot'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '12px 24px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Plots List */}
      <div style={{ display: 'grid', gap: '15px' }}>
        {plots.map((plot) => (
          <div
            key={plot.plot_id}
            style={{
              background: '#0f2419',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #2d5a45'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#4ade80' }}>
                  {plot.plot_name}
                </h3>
                <div style={{ color: '#888', fontSize: '14px' }}>
                  <div>Size: {plot.area_ha} hectares</div>
                  {plot.soil_type && <div>Soil: {plot.soil_type}</div>}
                  {plot.primary_crop && <div>Crop: {plot.primary_crop}</div>}
                  {plot.gps_lat && plot.gps_lng && (
                    <div style={{ marginTop: '8px', color: '#4ade80' }}>
                      📍 GPS: {plot.gps_lat.toFixed(6)}, {plot.gps_lng.toFixed(6)}
                      <br />
                      <a 
                        href={`https://www.google.com/maps?q=${plot.gps_lat},${plot.gps_lng}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#4ade80', fontSize: '12px' }}
                      >
                        View on map →
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleEdit(plot)}
                  style={{
                    padding: '8px 16px',
                    background: '#fbbf24',
                    color: '#0f2419',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(plot.plot_id)}
                  style={{
                    padding: '8px 16px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {plots.length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
            No plots yet. Click "Add Plot" to create one.
          </div>
        )}
      </div>
    </div>
  );
}